import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Inbox, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { api } from "../lib/api";
import { useMe } from "../lib/me";
import { StarterPackStep } from "./StarterPackStep";

type Step = "confirm_inbound" | "starter_pack" | "signature" | "go_live";

export default function SetupWizard() {
  const { me, refresh } = useMe();
  const [step, setStep] = useState<Step>(() => {
    if (!me.setupProgress?.firstInboundReceivedAt) return "confirm_inbound";
    if (!me.setupProgress?.starterPackInstalled) return "starter_pack";
    return "signature";
  });

  if (!me.tenant) return null;
  const inboundEmail = `support@${me.tenant.inboundSubdomain}`;

  return (
    <div className="min-h-full bg-gray-50">
      <div className="mx-auto max-w-2xl px-6 py-12">
        <header className="mb-8 flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500">Setup</span>
          <span className="text-gray-300">·</span>
          <span className="text-sm font-medium text-gray-900">{me.tenant.displayName}</span>
        </header>

        <Steps current={step} />

        <div className="mt-8 rounded-xl border border-gray-200 bg-white p-7 shadow-sm">
          {step === "confirm_inbound" && (
            <ConfirmInbound
              inboundEmail={inboundEmail}
              onDone={() => setStep("starter_pack")}
              alreadyReceived={!!me.setupProgress?.firstInboundReceivedAt}
            />
          )}
          {step === "starter_pack" && (
            <StarterPackStep
              onDone={async () => {
                await refresh();
                setStep("signature");
              }}
              onSkip={() => setStep("signature")}
            />
          )}
          {step === "signature" && (
            <SignatureStep
              tenantName={me.tenant.displayName}
              onDone={() => setStep("go_live")}
              onBack={() => setStep("starter_pack")}
            />
          )}
          {step === "go_live" && (
            <GoLiveStep
              onDone={async () => {
                await api.post("/api/setup/complete", { autoRepliesEnabled: false });
                await refresh();
              }}
              onBack={() => setStep("signature")}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Steps({ current }: { current: Step }) {
  const order: Step[] = ["confirm_inbound", "starter_pack", "signature", "go_live"];
  const labels: Record<Step, string> = {
    confirm_inbound: "Confirm inbound",
    starter_pack: "Starter pack",
    signature: "Signature",
    go_live: "Go live",
  };
  const idx = order.indexOf(current);
  return (
    <ol className="flex items-center justify-between w-full">
      {order.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <li key={s} className="flex items-center gap-2 flex-1 last:flex-initial">
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={
                  "grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-xs font-semibold " +
                  (done
                    ? "bg-gray-900 text-white"
                    : active
                      ? "bg-brand-600 text-white"
                      : "bg-gray-200 text-gray-500")
                }
              >
                {done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className={"text-sm whitespace-nowrap hidden sm:inline " + (active ? "font-medium text-gray-900" : "text-gray-500")}>
                {labels[s]}
              </span>
            </div>
            {i < order.length - 1 && (
              <span className="h-px bg-gray-200 flex-grow mx-2 min-w-[12px] sm:min-w-[32px]" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ConfirmInbound({
  inboundEmail,
  onDone,
  alreadyReceived,
}: {
  inboundEmail: string;
  onDone: () => void;
  alreadyReceived: boolean;
}) {
  const [received, setReceived] = useState(alreadyReceived);
  const [received_meta, setMeta] = useState<{ fromEmail?: string; subject?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (received) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get<{ received: boolean; fromEmail?: string; subject?: string }>(
          "/api/setup/inbound-test",
        );
        if (r.received && !stop) {
          setReceived(true);
          setMeta({ fromEmail: r.fromEmail, subject: r.subject });
        }
      } catch {
        // swallow; will retry next tick
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [received]);

  const copy = async () => {
    await navigator.clipboard.writeText(inboundEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (received) {
    return (
      <Section title="Got it!" subtitle="We received your test email. Inbound is verified.">
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm">
          <div className="flex items-center gap-2 font-medium text-emerald-900">
            <Check className="h-4 w-4" /> Email received
          </div>
          {received_meta && (
            <div className="mt-2 space-y-0.5 text-emerald-800/80">
              {received_meta.fromEmail && (
                <div>
                  <span className="text-emerald-700/80">From:</span> {received_meta.fromEmail}
                </div>
              )}
              {received_meta.subject && (
                <div>
                  <span className="text-emerald-700/80">Subject:</span> {received_meta.subject}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={onDone}>Continue →</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Step 1 — Confirm you can receive emails"
      subtitle="Send a test email from any inbox. We'll detect it within seconds."
    >
      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Your inbound address
        </div>
        <div className="mt-1 flex items-center justify-between gap-3">
          <code className="break-all font-mono text-sm text-gray-900">{inboundEmail}</code>
          <Button size="sm" variant="secondary" onClick={copy}>
            <Copy className="h-3.5 w-3.5" />
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-3 rounded-md border border-gray-200 px-4 py-3 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        <span>Waiting for first email…</span>
        <span className="ml-auto text-xs text-gray-400">Polling every 3s</span>
      </div>
      <p className="text-sm text-gray-500">
        Tip: emails can take 30–60 seconds to arrive. If nothing shows after 2 minutes, your DNS
        records may not be propagated yet.
      </p>
    </Section>
  );
}

function SignatureStep({
  tenantName,
  onDone,
  onBack,
}: {
  tenantName: string;
  onDone: () => void;
  onBack: () => void;
}) {
  const [signature, setSignature] = useState(`— Customer Care\n${tenantName}`);
  const [autoDisclaimer, setAutoDisclaimer] = useState(true);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch("/api/setup/signature", {
        plainText: signature,
        autoDisclaimerEnabled: autoDisclaimer,
      });
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      title="Step 2 — Reply signature"
      subtitle="This appears at the end of every reply Triage sends."
    >
      <textarea
        value={signature}
        onChange={(e) => setSignature(e.target.value)}
        rows={5}
        className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
      />
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={autoDisclaimer}
          onChange={(e) => setAutoDisclaimer(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300"
        />
        <span>
          Append a standard "automated response" disclaimer to every reply{" "}
          <span className="text-gray-500">(recommended)</span>
        </span>
      </label>
      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onSave} loading={saving}>
          Continue
        </Button>
      </div>
    </Section>
  );
}

function GoLiveStep({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [loading, setLoading] = useState(false);
  return (
    <Section title="You're set up" subtitle="Auto-replies stay OFF until you flip them on from Settings.">
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm">
        <div className="flex items-center gap-2 font-medium text-gray-900">
          <ShieldCheck className="h-4 w-4 text-gray-700" />
          What happens next
        </div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-gray-600">
          <li>Triage will receive emails at your inbound address.</li>
          <li>Drafts queue up for your review — nothing is sent automatically yet.</li>
          <li>You can wire up categories, workflows, and connectors from the dashboard.</li>
        </ul>
      </div>
      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button
          loading={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await onDone();
            } finally {
              setLoading(false);
            }
          }}
        >
          Take me to the dashboard →
        </Button>
      </div>
    </Section>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// keep useNavigate import even if unused yet — wizard may need it for sub-routes later
void useNavigate;
void Inbox;
